/**
 * IVA Ventas (Débito) — reporte profesional con 2 vistas:
 *  - Resumen D-104: por tarifa (Gravado 13/4/2/1/0, Exonerado, Exento) con base, IVA, exoneración.
 *  - Detalle por FE: cada documento del período con sus columnas fiscales.
 *
 * Filtros: período (YYYY-MM). Exports: Excel + PDF.
 */

import { useState, useEffect } from 'react';
import {
  FileSpreadsheet, FileText, Calendar, RefreshCw, Loader2,
  Receipt, DollarSign, FileBarChart, ShieldCheck, ClipboardList
} from 'lucide-react';
import dayjs from 'dayjs';
import toast from 'react-hot-toast';
import { reportesApi } from '../../utils/api';
import MonthRangePicker from '../../components/MonthRangePicker';

const fmtCRC = (v) => '₡' + (parseFloat(v) || 0).toLocaleString('es-CR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const TIPO_DOC = { '01': 'FE', '02': 'ND', '03': 'NC', '04': 'TE' };
const TIPO_DOC_COLORS = {
  '01': 'bg-blue-100 text-blue-700',
  '02': 'bg-amber-100 text-amber-700',
  '03': 'bg-red-100 text-red-700',
  '04': 'bg-cyan-100 text-cyan-700'
};

export default function IvaVentasDebito() {
  const [periodo, setPeriodo] = useState(dayjs().format('YYYY-MM'));
  // Wrapper para MonthRangePicker (modo-mes only — el reporte es por mes específico)
  const filtroFecha = (() => {
    const [y, m] = periodo.split('-').map(Number);
    const last = new Date(y, m, 0).getDate();
    return { modo: 'mes', periodo, desde: `${periodo}-01`, hasta: `${periodo}-${String(last).padStart(2, '0')}` };
  })();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('resumen');
  const [busqueda, setBusqueda] = useState('');
  const [exportingExcel, setExportingExcel] = useState(false);
  const [exportingPdf, setExportingPdf] = useState(false);

  const cargar = async () => {
    setLoading(true);
    try {
      const res = await reportesApi.getIvaVentasDebito({ periodo });
      if (res.data?.success) setData(res.data.data);
      else toast.error('Error al cargar reporte');
    } catch (e) { toast.error(e.response?.data?.error || 'Error al cargar'); }
    finally { setLoading(false); }
  };
  useEffect(() => { cargar(); /* eslint-disable-next-line */ }, [periodo]);

  const descargarExcel = async () => {
    setExportingExcel(true);
    try {
      const res = await reportesApi.exportIvaVentasDebito({ periodo });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url; a.download = `IVA_Ventas_Debito_${periodo}.xlsx`;
      a.click(); URL.revokeObjectURL(url);
      toast.success('Excel descargado');
    } catch (e) { toast.error('Error al descargar Excel'); }
    finally { setExportingExcel(false); }
  };

  const descargarPdf = async () => {
    setExportingPdf(true);
    try {
      const res = await reportesApi.exportIvaVentasDebitoPdf({ periodo });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url; a.download = `IVA_Ventas_Debito_${periodo}.pdf`;
      a.click(); URL.revokeObjectURL(url);
      toast.success('PDF descargado');
    } catch (e) { toast.error('Error al descargar PDF'); }
    finally { setExportingPdf(false); }
  };

  const detalleFiltrado = (data?.detalle || []).filter(d => {
    if (!busqueda) return true;
    const q = busqueda.toLowerCase();
    return (d.cliente || '').toLowerCase().includes(q) ||
           (d.consecutivo || '').toLowerCase().includes(q);
  });

  const tarifaColor = (t) => {
    if (t.startsWith('EX')) return 'bg-amber-50 border-l-amber-400';
    if (t === '00' || t === 'NS') return 'bg-gray-50 border-l-gray-300';
    return 'bg-emerald-50 border-l-emerald-400';
  };

  return (
    <div className="w-full flex flex-col h-[var(--alto-contenido)]">
      {/* Header + filtros */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm shrink-0 mb-4">
        <div className="px-5 py-4 flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="p-2.5 rounded-lg bg-emerald-100">
              <ShieldCheck className="h-6 w-6 text-emerald-600" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">IVA Ventas (Débito)</h2>
              <p className="text-sm text-gray-500">Detalle de IVA cobrado en ventas para declaración D-104</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={descargarPdf} disabled={exportingPdf || loading}
              className="px-4 py-2 border-2 border-red-400 text-red-600 rounded-lg hover:bg-red-50 text-sm font-semibold flex items-center gap-2 disabled:opacity-50">
              {exportingPdf ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4" />}
              PDF
            </button>
            <button onClick={descargarExcel} disabled={exportingExcel || loading}
              className="px-4 py-2 border-2 border-emerald-500 text-emerald-600 rounded-lg hover:bg-emerald-50 text-sm font-semibold flex items-center gap-2 disabled:opacity-50">
              {exportingExcel ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileSpreadsheet className="h-4 w-4" />}
              Excel
            </button>
          </div>
        </div>
        <div className="px-5 py-3 border-t border-gray-100 flex items-center gap-3 flex-wrap">
          <MonthRangePicker
            value={filtroFecha}
            onChange={(v) => setPeriodo(v.periodo)}
            onRefresh={cargar}
            onlyMonth
          />
          {tab === 'detalle' && (
            <>
              <div className="flex-1" />
              <input type="text" placeholder="Buscar consecutivo o cliente…" value={busqueda} onChange={(e) => setBusqueda(e.target.value)}
                className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm w-64 focus:outline-none focus:ring-2 focus:ring-emerald-200" />
            </>
          )}
        </div>
      </div>

      {/* KPI cards */}
      {data && (
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 shrink-0 mb-4">
          <KPICard color="emerald" Icon={Receipt} label="Documentos" value={data.resumen.totalDocs} sub="emitidos" />
          <KPICard color="blue" Icon={FileBarChart} label="Base Gravada" value={fmtCRC(data.resumen.totalGravadoBaseCRC)} sub="con IVA cobrado" />
          <KPICard color="amber" Icon={FileBarChart} label="Base Exonerada" value={fmtCRC(data.resumen.totalExoneradoBaseCRC)} sub="sin IVA cobrado" />
          <KPICard color="cyan" Icon={DollarSign} label="IVA Cobrado" value={fmtCRC(data.resumen.totalIVACRC)} sub="A débito D-104" highlight />
          <KPICard color="purple" Icon={DollarSign} label="IVA Exonerado" value={fmtCRC(data.resumen.totalIVAExoneradoCRC)} sub="no cobrado" />
        </div>
      )}

      {/* Tabs + tabla */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden flex-1 min-h-0 flex flex-col">
        <div className="border-b border-gray-200 shrink-0">
          <nav className="flex gap-1 px-4">
            <TabButton active={tab === 'resumen'} onClick={() => setTab('resumen')} Icon={ShieldCheck}>Resumen D-104</TabButton>
            <TabButton active={tab === 'detalle'} onClick={() => setTab('detalle')} Icon={ClipboardList}>Detalle por FE</TabButton>
          </nav>
        </div>

        {loading ? (
          <div className="flex-1 flex items-center justify-center"><Loader2 className="h-8 w-8 text-emerald-500 animate-spin" /></div>
        ) : !data ? (
          <div className="flex-1 flex flex-col items-center justify-center text-gray-400">
            <ShieldCheck className="h-12 w-12 mb-3 text-gray-300" />
            <p className="text-sm">Sin datos para el período seleccionado</p>
          </div>
        ) : tab === 'resumen' ? (
          <div className="flex-1 min-h-0 overflow-auto">
            <table className="w-full text-sm">
              <thead className="sticky top-0 z-10 bg-emerald-50 shadow-[0_1px_0_rgba(229,231,235,1)]">
                <tr className="border-b border-emerald-200">
                  <th className="text-left px-4 py-3 font-semibold text-emerald-700">Categoría</th>
                  <th className="text-right px-3 py-3 font-semibold text-emerald-700">Documentos</th>
                  <th className="text-right px-3 py-3 font-semibold text-emerald-700">Base Imp. CRC</th>
                  <th className="text-right px-3 py-3 font-semibold text-emerald-700">IVA Exonerado CRC</th>
                  <th className="text-right px-3 py-3 font-semibold text-emerald-700">IVA Cobrado CRC</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {data.porTarifa.map(t => (
                  <tr key={t.tarifa} className={`${tarifaColor(t.tarifa)} border-l-4 hover:bg-emerald-50/40`}>
                    <td className="px-4 py-2.5 font-medium text-gray-900">{t.label}</td>
                    <td className="px-3 py-2.5 text-right text-gray-700">{t.docs}</td>
                    <td className="px-3 py-2.5 text-right font-mono text-gray-700">{fmtCRC(t.baseCRC)}</td>
                    <td className="px-3 py-2.5 text-right font-mono text-amber-700">{fmtCRC(t.exoneracionCRC)}</td>
                    <td className="px-3 py-2.5 text-right font-mono font-semibold text-emerald-700">{fmtCRC(t.ivaCRC)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="sticky bottom-0 z-10 bg-emerald-100 border-t-2 border-emerald-300 shadow-[0_-1px_0_rgba(156,163,175,1)]">
                <tr className="font-bold text-emerald-900">
                  <td className="px-4 py-3">TOTAL</td>
                  <td className="px-3 py-3 text-right">{data.resumen.totalDocs}</td>
                  <td className="px-3 py-3 text-right font-mono">{fmtCRC(data.resumen.baseImponibleTotalCRC)}</td>
                  <td className="px-3 py-3 text-right font-mono">{fmtCRC(data.resumen.totalIVAExoneradoCRC)}</td>
                  <td className="px-3 py-3 text-right font-mono">{fmtCRC(data.resumen.totalIVACRC)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        ) : (
          <div className="flex-1 min-h-0 overflow-auto">
            <table className="w-full text-sm">
              <thead className="sticky top-0 z-10 bg-emerald-50 shadow-[0_1px_0_rgba(229,231,235,1)]">
                <tr className="border-b border-emerald-200">
                  <th className="text-left px-3 py-3 font-semibold text-emerald-700">Fecha</th>
                  <th className="text-left px-3 py-3 font-semibold text-emerald-700">Consecutivo</th>
                  <th className="text-center px-3 py-3 font-semibold text-emerald-700">Tipo</th>
                  <th className="text-left px-3 py-3 font-semibold text-emerald-700">Cliente</th>
                  <th className="text-right px-3 py-3 font-semibold text-emerald-700">Gravado CRC</th>
                  <th className="text-right px-3 py-3 font-semibold text-emerald-700">Exento CRC</th>
                  <th className="text-right px-3 py-3 font-semibold text-emerald-700">Exonerado CRC</th>
                  <th className="text-right px-3 py-3 font-semibold text-emerald-700">IVA CRC</th>
                  <th className="text-right px-3 py-3 font-semibold text-emerald-700">Total CRC</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {detalleFiltrado.map(d => {
                  const sign = d.tipoDocumento === '03' ? -1 : 1;
                  const rowClass = sign === -1 ? 'text-red-600' : 'text-gray-700';
                  return (
                    <tr key={d.id} className="hover:bg-emerald-50/40">
                      <td className="px-3 py-2 text-gray-600 whitespace-nowrap">{dayjs(d.fechaEmision).format('DD/MM/YYYY')}</td>
                      <td className="px-3 py-2 font-mono text-xs text-gray-700">{d.consecutivo}</td>
                      <td className="px-3 py-2 text-center">
                        <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${TIPO_DOC_COLORS[d.tipoDocumento] || 'bg-gray-100'}`}>
                          {TIPO_DOC[d.tipoDocumento] || d.tipoDocumento}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-gray-800">{d.cliente}</td>
                      <td className={`px-3 py-2 text-right font-mono ${rowClass}`}>{fmtCRC(d.gravadoCRC * sign)}</td>
                      <td className={`px-3 py-2 text-right font-mono ${rowClass}`}>{fmtCRC(d.exentoCRC * sign)}</td>
                      <td className={`px-3 py-2 text-right font-mono ${rowClass}`}>{fmtCRC(d.exoneradoCRC * sign)}</td>
                      <td className={`px-3 py-2 text-right font-mono font-semibold ${sign === -1 ? 'text-red-600' : 'text-emerald-700'}`}>{fmtCRC(d.ivaCRC * sign)}</td>
                      <td className={`px-3 py-2 text-right font-mono ${rowClass}`}>{fmtCRC(d.totalCRC * sign)}</td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot className="sticky bottom-0 z-10 bg-emerald-100 border-t-2 border-emerald-300 shadow-[0_-1px_0_rgba(156,163,175,1)]">
                {(() => {
                  const sums = detalleFiltrado.reduce((acc, d) => {
                    const s = d.tipoDocumento === '03' ? -1 : 1;
                    acc.g += (d.gravadoCRC || 0) * s;
                    acc.e += (d.exentoCRC || 0) * s;
                    acc.ex += (d.exoneradoCRC || 0) * s;
                    acc.iva += (d.ivaCRC || 0) * s;
                    acc.t += (d.totalCRC || 0) * s;
                    return acc;
                  }, { g: 0, e: 0, ex: 0, iva: 0, t: 0 });
                  return (
                    <tr className="font-bold text-emerald-900">
                      <td colSpan={4} className="px-3 py-3 text-right">TOTAL ({detalleFiltrado.length} documentos)</td>
                      <td className="px-3 py-3 text-right font-mono">{fmtCRC(sums.g)}</td>
                      <td className="px-3 py-3 text-right font-mono">{fmtCRC(sums.e)}</td>
                      <td className="px-3 py-3 text-right font-mono">{fmtCRC(sums.ex)}</td>
                      <td className="px-3 py-3 text-right font-mono">{fmtCRC(sums.iva)}</td>
                      <td className="px-3 py-3 text-right font-mono">{fmtCRC(sums.t)}</td>
                    </tr>
                  );
                })()}
              </tfoot>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function KPICard({ color, Icon, label, value, sub, highlight }) {
  const colors = {
    purple: 'from-purple-50 to-purple-100 border-purple-200',
    blue: 'from-blue-50 to-blue-100 border-blue-200',
    cyan: 'from-cyan-50 to-cyan-100 border-cyan-200',
    amber: 'from-amber-50 to-amber-100 border-amber-200',
    emerald: 'from-emerald-50 to-emerald-100 border-emerald-200'
  };
  const iconBg = { purple: 'bg-purple-600', blue: 'bg-blue-600', cyan: 'bg-cyan-600', amber: 'bg-amber-600', emerald: 'bg-emerald-600' };
  return (
    <div className={`bg-white rounded-lg border shadow-sm overflow-hidden ${highlight ? 'ring-2 ring-emerald-300' : 'border-gray-200'}`}>
      <div className={`px-3 py-2 bg-gradient-to-r ${colors[color]} border-b flex items-center gap-2`}>
        <div className={`p-1 ${iconBg[color]} rounded`}><Icon className="h-3.5 w-3.5 text-white" /></div>
        <h3 className="font-semibold text-xs text-gray-800">{label}</h3>
      </div>
      <div className="px-3 py-2.5">
        <div className={`text-lg font-bold leading-tight ${highlight ? 'text-emerald-700' : 'text-gray-900'}`}>{value}</div>
        {sub && <div className="text-[11px] text-gray-400 mt-1">{sub}</div>}
      </div>
    </div>
  );
}

function TabButton({ active, onClick, Icon, children }) {
  return (
    <button onClick={onClick}
      className={`py-2.5 px-4 border-b-2 text-sm font-medium transition-colors flex items-center gap-2 ${
        active ? 'border-emerald-500 text-emerald-700' : 'border-transparent text-gray-500 hover:text-gray-700'
      }`}>
      <Icon className="h-4 w-4" /> {children}
    </button>
  );
}
